/**
 * Garden placements — harvested flowers the player has placed as living decoration
 * in the iso garden. Each placement is one plant instance at a free garden-unit
 * position (gx, gy). Placement is FREE (anywhere in the plot) but COLLISION-checked
 * (no overlapping another placement) and excludes RESTRICTED areas (the pond, and a
 * small margin off the exposed plot edges).
 *
 * The inventory model: `stock[species]` is the total harvested of that species; the
 * number PLACED in the garden is subtracted to get what's still in the bag. So
 * placing consumes availability (not the stock count itself) and removing frees it.
 * Inventory + Market always show AVAILABLE = stock − placed.
 */

import { type FlowerId, isTrailing } from "./flowers";
import { inPlot, CELL, type Plot } from "../render/plot";
import { decorDef, isHost, hostSlots, type DecorPlacement } from "./decor";
import { discHitsFence, fencesNear, FENCE_HALF_T } from "./fenceGeom";

/** One placed plant in the garden. `id` is a stable unique key (for React + removal). `hostId`, when set,
 *  anchors this plant to a HOST decor (planter/basket/urn) — it renders on that host's rim instead of on
 *  the ground, and follows the host. Trailing plants REQUIRE a host. */
export interface GardenPlacement {
  id: string;
  species: FlowerId;
  /** Position in garden units (same space as the plot cells). Ignored when `hostId` is set (the plant
   *  takes the host's position). */
  gx: number;
  gy: number;
  /** the decor id of the HOST this plant is planted on (a planter/basket/urn), or undefined for ground. */
  hostId?: string;
  /** WHICH planting slot of that host this plant occupies (0-based, along the host's length). Only the
   *  long planter box has more than one; everything else is slot 0, which is also what an older save that
   *  predates multi-slot hosts reads back as — and slot 0 of a one-slot host is the rim itself, so those
   *  plants do not move. Meaningless without `hostId`. */
  slot?: number;
}

/** BASE coin cost of the FIRST purchased plot. The price of every later plot is derived from this by
 *  `plotCellPrice()` — do not read this constant directly as "the price of a plot". */
export const PLOT_CELL_PRICE = 500;

/**
 * THE HARD CEILING on how many cells a garden can own, counting the FREE STARTING CELL — so a fresh
 * garden reads 1 of 50 and the player buys 49.
 *
 * THIS IS AN EXPANSION-FACING BALANCE NUMBER: 50 is the maximum FOR NOW and is expected to be raised when
 * the world grows, so it lives once, here, and every other file reads it. Nothing may hard-code 50 — not
 * the expand tool, not the smoke test, not an achievement threshold.
 *
 * TWO THINGS MOVE WITH IT. (1) The "The Whole Garden" achievement (`sim/achievements.ts`) is a 10-point
 * COMPLETION whose threshold IS this number: raise the cap without raising the achievement and it stops reading
 * as a completion; lower the cap without lowering it and the achievement turns unreachable. (2) `plotCellPrice`
 * is now only ever asked for purchase counts 0…MAX_PLOT_CELLS−2, so the tail of the tiered curve never has
 * to stay sane past there.
 *
 * WHAT IT IS NOT: it is not a clamp applied to a loaded save. An over-cap save (a dev account, a rollback
 * from a future expansion that shipped a bigger number) LOADS AND RENDERS AT ITS TRUE SIZE — see
 * `coercePlot`. The cap gates BUYING, not owning.
 */
export const MAX_PLOT_CELLS = 50;

/** Is the garden full — can no more ground be bought? Takes the OWNED cell count, starter cell included.
 *  `>=` rather than `===` on purpose: an over-cap save is at its maximum too, and must not be offered a
 *  51st tile because its count sailed past the equality. */
export function plotAtMax(ownedCells: number): boolean {
  return ownedCells >= MAX_PLOT_CELLS;
}

/**
 * WHAT A PLOT COSTS — a TIERED GEOMETRIC curve on the number already purchased, not a flat fee.
 *
 * The flat 500 it replaced was not a price, it was a rounding error: greenhouse income is
 * `pots × COINS_PER_MIN` (12→60 pots = 120→600 coins/min), so a simulated active player could buy
 * **7,591 plots in 300 hours** (reports/plot-pricing.html), and BALANCE.md §4 already records that "coins
 * are never the bottleneck … an optimal player ends with millions to spare". The garden is now walled at `MAX_PLOT_CELLS`, but a wall
 * is not a brake — this curve is the only thing pacing the 49 purchases that lead up to it.
 *
 * WHY TIERED AND NOT ONE RATE. A single geometric rate cannot serve both ends. Simulated against the real
 * economy, ×1.18 flat reached 84,500 coins by plot #32 with an 8.5-HOUR wait between purchases — the late
 * game becomes a spreadsheet. ×1.12 flat stayed cheap enough that plot #30 cost under 2 hours of income,
 * which is the flat-500 problem again wearing a hat. So the rate DECAYS as the garden grows:
 *
 *     plots 1–8    ×1.22   the bite that makes early expansion a real choice against seeds/shelves
 *     plots 9–20   ×1.15   the main ramp — each plot is a goal worth a session
 *     plots 21+    ×1.10   a soft tail: still rising, never doubling out of reach
 *
 * WHAT THE CURVE PRODUCES (active player, 90% uptime, simulated vs the real tables):
 *     plot  #5  1,100c @ 0.3h      plot #20  11,500c @ 3.1h  (wait 1.4h)
 *     plot #10  2,800c @ 0.9h      plot #25  19,000c @ 5.6h  (wait 2.5h)
 *     plot #15  5,700c @ 1.7h      plot #30  31,000c @ 9.7h  (wait 4.1h)
 * The WAIT BETWEEN PURCHASES is the number that was tuned, not the price: it climbs smoothly from minutes
 * to a few hours, so expansion always feels reachable and never free. A casual player (45% uptime) trails
 * by roughly 2× and still owns 20 plots inside a work week of play.
 *
 * Prices are ROUNDED to readable steps (100s, then 500s, then 1000s) — a plot costs "2,800", never
 * "2,764". The rounding is part of the price: it is what the ghost tile shows the player.
 */
export function plotCellPrice(purchasedCount: number): number {
  let p = PLOT_CELL_PRICE;
  for (let i = 0; i < purchasedCount; i++) p *= i < 8 ? 1.22 : i < 20 ? 1.15 : 1.10;
  return roundPrice(p);
}

/** Round a raw price to a readable step so the tile never shows a figure like 2,764. */
function roundPrice(v: number): number {
  if (v < 10000) return Math.round(v / 100) * 100;
  if (v < 100000) return Math.round(v / 500) * 500;
  return Math.round(v / 1000) * 1000;
}

/** What selling a plot refunds: **75%** of WHAT THAT PLOT COST — a 25% haircut (the rate this has always
 *  used), so undoing an expansion costs something but is not punishing. Priced off the plot being given
 *  back (`purchasedCount - 1`, the last one bought), never off the flat base. Without that the curve is an
 *  arbitrage machine: buy cheap at plot #2, sell back at plot #30 prices. The haircut must stay BELOW 100%
 *  for the same reason. */
export const PLOT_REFUND_RATE = 0.75;
export function plotSellRefund(purchasedCount: number): number {
  if (purchasedCount <= 0) return 0;
  return Math.round(plotCellPrice(purchasedCount - 1) * PLOT_REFUND_RATE);
}
/** MILD collision radius (garden units) around a placed plant's BASE — others must
 *  clear ~2× this. Kept small so plants can sit close together / cluster naturally. */
export const PLACE_RADIUS = 0.32;
/** Small inset (garden units) kept clear of the plot's exposed outer edge. */
export const PLACE_EDGE_MARGIN = 0.15;

/** The decorative scenery TREE's ANCHOR position (garden units) — the starting OAK decor is placed here.
 *  (Formerly a hardcoded scene tree; it's now a real, movable/sellable oakTree decor item.) */
export const TREE_BASE = { gx: -1.8, gy: -1.8 } as const;
/** The starting OAK decor placement (the tree every garden begins with). A fixed id + seed so it renders
 *  identically to the old scene tree and can be de-duped across loads. */
export const STARTER_OAK: DecorPlacement = { id: "starter-oak", decor: "oakTree", gx: TREE_BASE.gx, gy: TREE_BASE.gy, seed: 7 };

/** How many of `species` are currently placed in the garden. */
export function placedCount(placements: GardenPlacement[], species: FlowerId): number {
  let n = 0;
  for (const p of placements) if (p.species === species) n++;
  return n;
}

/** AVAILABLE (still in the bag) for a species = harvested stock − placed. Clamped ≥0. */
export function availableStock(stockCount: number, placements: GardenPlacement[], species: FlowerId): number {
  return Math.max(0, stockCount - placedCount(placements, species));
}


// ---- player-painted WATER (a fine brush-painted cell grid) -----------------
// Water is stored as a SET of painted cells on a fine sub-grid that spans the whole
// plot (independent of the plot cells). A round brush paints/erases cells. Water may
// NOT be painted under a decoration; grass is excluded from any watered cell; and a
// plant can't be placed on water. Rendered as a smooth blobby pool from the cells.
//
// HOW THIS IS SAVED — read before making the grid finer or touching the format.
// In memory, one painted sub-cell is one `"wx,wy"` key. ON THE WIRE (localStorage mirror,
// cloud PUT) the save carries ROW RUNS instead — see `packCells` below: `"wy:x+len,x;wy:…"`,
// one run per contiguous stretch of a row. Painted water is overwhelmingly contiguous along a
// row, so a MAX_PLOT_CELLS = 50 garden painted edge to edge — 24,200 sub-cells, which was ~590 KB
// of per-key JSON (water + terrain) — now packs to ~3.5 KB. The per-key form broke cloud save
// twice: over the 64 KiB `keepalive` body cap, and past the server's old 256kb express.json limit.
// Loading still accepts the OLD per-key array (and terrain's per-key record) forever: every save
// written before the change is in that form, and the server's coerce passes either through.
// A run's cost is per ROW, not per cell — so raising WATER_DIV now costs linearly in rows, but
// a checkerboard-like pattern is still the worst case (one run per cell). Keep the unpack cap
// (MAX_UNPACKED_CELLS) in step with any cap raise: a tiny packed string can claim a huge area.

/** Painted-water sub-grid resolution: cells PER plot-cell (CELL units) on each axis.
 *  MUST BE EVEN so the plot EDGES (at ±CELL/2 in garden units) land exactly on a cell
 *  boundary: a plot edge is at N·WATER_CELL where N = WATER_DIV/2, which is an integer
 *  only when WATER_DIV is even. 22 keeps ~21 cells/side while aligning perfectly. */
export const WATER_DIV = 22;
/** Garden-unit size of one water cell. */
export const WATER_CELL = CELL / WATER_DIV;
/** Water brush RADIUS in garden units. This is the natural width of a single painted channel, and
 *  the geometric basis for the pond/stream cutoff (see sim/waterType.ts): a one-stroke channel is a
 *  stream; water much wider than the brush is a pond. Single source of truth (App + classifier). */
export const WATER_BRUSH_R = 1.35;
/** Brush radius expressed in WATER CELLS (≈ the half-width of a single-stroke channel). */
export const WATER_BRUSH_R_CELLS = WATER_BRUSH_R / WATER_CELL;
/** Scroll range of the fractional (round) water brush radius, garden units. */
export const WATER_BRUSH_MIN = 0.7, WATER_BRUSH_MAX = 5.5, WATER_BRUSH_STEP = 0.35;

/** WATER BRUSH SHAPES, on the terrain painter's sub-grid (sim/terrain.ts):
 *  `round`  the fractional circle (the original brush, and still the default);
 *  `block`  an N×N block of WHOLE cells snapped to the grid, sized in whole-cell steps — the footprint the
 *           terrain's stone path lays, so water can butt cleanly against a paver run.
 *  The eraser offers `round` and `square`; as with the terrain eraser, its square IS the snapped block, so
 *  a block of water comes up in the same whole cells it went down in. */
export type WaterBrushShape = "round" | "block";
export type WaterEraseShape = "round" | "square";
/** Block brush / square eraser size, whole cells PER SIDE. 7 ≈ the round brush's default diameter. */
export const WATER_BLOCK_CELLS = 7;
export const WATER_BLOCK_CELLS_MIN = 1;
export const WATER_BLOCK_CELLS_MAX = 25;

// Water cells are CORNER-INDEXED: cell (wx,wy) occupies garden box
//   [wx·WC, (wx+1)·WC] × [wy·WC, (wy+1)·WC]
// so its corners are exactly the lattice points (wx,wy)…(wx+1,wy+1). The lattice is
// anchored at garden 0, and since CELL/2 = (WATER_DIV/2)·WC (WATER_DIV even), EVERY plot
// edge (±CELL/2 from a cell centre that's a multiple of CELL) lands on a lattice line.
// So the cells TILE the grass EXACTLY — no straddle onto the soil, and the rendered water
// lines up with the painted cells with no offset fudge.

/** The water-cell whose box contains garden point (gx, gy). */
export function waterCellAt(gx: number, gy: number): { wx: number; wy: number } {
  return { wx: Math.floor(gx / WATER_CELL), wy: Math.floor(gy / WATER_CELL) };
}
export function waterKey(wx: number, wy: number): string { return `${wx},${wy}`; }
/** Garden-unit CENTRE of a water cell (box midpoint). */
export function waterCellCenter(wx: number, wy: number): { gx: number; gy: number } {
  return { gx: (wx + 0.5) * WATER_CELL, gy: (wy + 0.5) * WATER_CELL };
}

/** Is a water cell's WHOLE BOX inside the plot? Tests all four box corners (with a tiny
 *  inward epsilon for float safety) — so an edge cell whose box would poke past the plot
 *  boundary is rejected, and the rendered water never spills past the green. */
export function cellInPlot(plot: Plot, wx: number, wy: number): boolean {
  const e = WATER_CELL * 1e-3;
  const x0 = wx * WATER_CELL + e, x1 = (wx + 1) * WATER_CELL - e;
  const y0 = wy * WATER_CELL + e, y1 = (wy + 1) * WATER_CELL - e;
  return inPlot(plot, x0, y0, 0) && inPlot(plot, x1, y0, 0)
    && inPlot(plot, x1, y1, 0) && inPlot(plot, x0, y1, 0);
}

/** Is garden point (gx, gy) on PAINTED water? */
export function inPaintedWater(water: ReadonlySet<string>, gx: number, gy: number): boolean {
  const { wx, wy } = waterCellAt(gx, gy);
  return water.has(waterKey(wx, wy));
}

/** Is (gx, gy) water? Tests CELL membership in the (effective) water set — the water is
 *  exactly the painted cells, so this is authoritative for collision + grass exclusion. */
export function isWaterAt(water: ReadonlySet<string>, gx: number, gy: number): boolean {
  return inPaintedWater(water, gx, gy);
}

/** Is there ANY water cell whose centre is within `radius` (garden units) of (gx, gy)? Used so a decor
 *  item's whole FOOTPRINT is kept off the water (not just its centre) — an item can't be placed where it
 *  would overlap the water at all. */
export function waterWithin(water: ReadonlySet<string>, gx: number, gy: number, radius: number): boolean {
  if (isWaterAt(water, gx, gy)) return true;
  const r2 = radius * radius;
  const c0 = waterCellAt(gx - radius, gy - radius), c1 = waterCellAt(gx + radius, gy + radius);
  for (let wx = c0.wx; wx <= c1.wx; wx++) {
    for (let wy = c0.wy; wy <= c1.wy; wy++) {
      if (!water.has(waterKey(wx, wy))) continue;
      const cc = waterCellCenter(wx, wy);
      if ((cc.gx - gx) ** 2 + (cc.gy - gy) ** 2 <= r2) return true;
    }
  }
  return false;
}

/**
 * Erode a water-cell set by one cell on every side: a cell survives only if all four of
 * its orthogonal neighbours are also water. Repeated erosion gives the concentric DEPTH
 * BANDS — each band is the water area inset by one more grid cell (so the deep centre is
 * the most-eroded set). Pure; returns a new Set.
 */
/**
 * Grow a water-cell set OUTWARD by one cell on every side (the inverse of erodeCells): the result is the
 * cells plus any 4-neighbour of a cell. Repeated dilation gives concentric OUTER rings — used to draw the
 * sandy BANK that rings the water and fades into the grass. Pure; returns a new Set.
 */
export function dilateCells(cells: ReadonlySet<string>): Set<string> {
  const out = new Set(cells);
  for (const key of cells) {
    const [wx, wy] = key.split(",").map(Number) as [number, number];
    out.add(`${wx - 1},${wy}`); out.add(`${wx + 1},${wy}`);
    out.add(`${wx},${wy - 1}`); out.add(`${wx},${wy + 1}`);
  }
  return out;
}

export function erodeCells(cells: ReadonlySet<string>): Set<string> {
  const out = new Set<string>();
  for (const key of cells) {
    const [wx, wy] = key.split(",").map(Number) as [number, number];
    if (cells.has(`${wx - 1},${wy}`) && cells.has(`${wx + 1},${wy}`)
      && cells.has(`${wx},${wy - 1}`) && cells.has(`${wx},${wy + 1}`)) {
      out.add(key);
    }
  }
  return out;
}

/**
 * Like `erodeCells`, but a neighbour that lies OUTSIDE the plot counts as "water" — so a cell
 * against an EXPOSED PLOT EDGE is NOT eroded. The depth bands therefore run flush to the plot
 * boundary (deepest water all the way to the edge), making a stream look like it flows off-plot
 * rather than tapering to a shallow bank at the boundary. Pure; returns a new Set.
 */
export function erodeCellsInterior(cells: ReadonlySet<string>, plot: Plot): Set<string> {
  const out = new Set<string>();
  const ok = (wx: number, wy: number) => cells.has(`${wx},${wy}`) || !cellInPlot(plot, wx, wy);
  for (const key of cells) {
    const [wx, wy] = key.split(",").map(Number) as [number, number];
    if (ok(wx - 1, wy) && ok(wx + 1, wy) && ok(wx, wy - 1) && ok(wx, wy + 1)) out.add(key);
  }
  return out;
}

/**
 * PRESERVE STREAMS ACROSS A PLOT EXPANSION.
 *
 * A channel reads as a "stream" partly because one end flows out an EXPOSED plot edge. Buying a new plot
 * that wraps that edge would silently turn the channel into a pond (its outlet is now interior). Instead of
 * remembering the old outlet, we EXTEND the water: for each stream mouth whose outlet the new plot just
 * swallowed, we carry the channel ACROSS the new cell(s) to a fresh outer edge — so it stays a real,
 * shape-valid stream, and the player can freely erase/edit the generated extension.
 *
 * Driven PER newly-inside sub-cell region, so a mouth bisected by a plot-boundary line only extends the
 * half bordering the newly-bought cell; the other half keeps its own still-exposed outlet (extended later
 * if/when that side is bought). Pure: returns a NEW water set (or the same ref if nothing changed).
 */
const WDIRS: ReadonlyArray<readonly [number, number]> = [[1, 0], [-1, 0], [0, 1], [0, -1]];
export function extendStreamsAcrossExpansion(
  water: ReadonlySet<string>, oldPlot: Plot, newPlot: Plot,
): Set<string> {
  if (water.size === 0) return water as Set<string>;
  const wasOutside = (wx: number, wy: number) => !cellInPlot(oldPlot, wx, wy);
  const nowInside = (wx: number, wy: number) => cellInPlot(newPlot, wx, wy);
  const touchesNewEdge = (wx: number, wy: number) => {
    for (const [dx, dy] of WDIRS) if (!cellInPlot(newPlot, wx + dx, wy + dy)) return true;
    return false;
  };
  const MAX_MARCH = WATER_DIV * 6;   // safety bound (a few plot-cells deep)
  const out = new Set(water);
  let changed = false;

  // 1. Find OUTLET STEPS: (water cell, direction) where the outward neighbour WAS outside the old plot and
  //    is NOW inside — the channel's exit that the new plot just swallowed. Bucket by direction so we can
  //    group a single mouth (a contiguous run of outlet cells along the perpendicular axis).
  const outletsByDir = new Map<string, Array<[number, number]>>();   // "dx,dy" → [wx,wy] (the WATER cell)
  for (const key of water) {
    const [wx, wy] = key.split(",").map(Number) as [number, number];
    for (const [dx, dy] of WDIRS) {
      const nx = wx + dx, ny = wy + dy;
      if (wasOutside(nx, ny) && nowInside(nx, ny) && !water.has(`${nx},${ny}`)) {
        const dk = `${dx},${dy}`;
        (outletsByDir.get(dk) ?? outletsByDir.set(dk, []).get(dk)!).push([wx, wy]);
      }
    }
  }

  // 2. Per direction, GROUP outlet cells into contiguous MOUTHS (runs along the perpendicular axis), and
  //    march ONE meandering spine from each mouth's CENTRE — width = the mouth's actual width. This avoids
  //    one wide mouth spawning many parallel bands (which floods + reads as a pond).
  for (const [dk, cells] of outletsByDir) {
    const [dx, dy] = dk.split(",").map(Number) as [number, number];
    const px = dy, py = dx;                                 // perpendicular axis
    const inSet = new Set(cells.map(([x, y]) => `${x},${y}`));
    const claimed = new Set<string>();
    for (const [sx, sy] of cells) {
      const sk = `${sx},${sy}`; if (claimed.has(sk)) continue;
      // grow the run along ±perp to find this mouth's extent + centre.
      let lo = 0, hi = 0;
      claimed.add(sk);
      for (let s = 1; ; s++) { const k = `${sx + px * s},${sy + py * s}`; if (!inSet.has(k)) break; claimed.add(k); hi = s; }
      for (let s = 1; ; s++) { const k = `${sx - px * s},${sy - py * s}`; if (!inSet.has(k)) break; claimed.add(k); lo = s; }
      // Only a NARROW mouth is a stream outlet worth extending. A WIDE mouth is a POND touching the new
      // plot — extending it would sprout a fat wedge (and a pond doesn't need to "flow off-plot"). Skip it.
      const mouthWidth = hi + lo + 1;
      if (mouthWidth > WATER_BRUSH_R_CELLS * 2.4) continue;
      const halfW = Math.max(1, Math.round((hi + lo) / 2) || Math.round(WATER_BRUSH_R_CELLS));
      const midOff = (hi - lo) / 2;
      let cx = Math.round(sx + px * midOff) + dx, cy = Math.round(sy + py * midOff) + dy;   // one step into the new area
      // deterministic per-mouth meander (no Math.random → stable across re-renders/saves).
      const h = ((cx * 73856093) ^ (cy * 19349663) ^ (dx * 83492791) ^ (dy * 2654435761)) >>> 0;
      const amp = 2 + (h % 3), wavelen = 10 + ((h >> 3) % 12), phase0 = ((h >> 7) % 100) / 100 * Math.PI * 2;
      let lateral = 0;
      for (let steps = 1; steps <= MAX_MARCH; steps++) {
        const targetLat = Math.round(Math.sin(phase0 + (steps / wavelen) * Math.PI * 2) * amp);
        if (targetLat > lateral) lateral++; else if (targetLat < lateral) lateral--;
        let atEdge = false;
        for (let w = -halfW; w <= halfW; w++) {
          const bx = cx + px * (w + lateral), by = cy + py * (w + lateral);
          if (nowInside(bx, by)) { if (!out.has(`${bx},${by}`)) { out.add(`${bx},${by}`); changed = true; } if (touchesNewEdge(bx, by)) atEdge = true; }
        }
        if (atEdge) break;                     // reached a fresh outer edge → new outlet made
        const ax = cx + dx, ay = cy + dy;
        if (!nowInside(ax, ay)) break;         // ran off the new area; stop
        cx = ax; cy = ay;
      }
    }
  }
  return changed ? out : (water as Set<string>);
}

export type WaterLoop = Array<{ x: number; y: number }>;   // garden-unit polygon

/**
 * Trace the OUTLINE of a water-cell set into closed garden-unit polygons (one solid mass
 * per connected region; interior holes become inner loops), then lightly ROUND the
 * corners. Cells are corner-indexed, so a boundary lattice point (px,py) is exactly the
 * cell corner at (px·WC, py·WC) — the outline lands precisely on the painted cells (and
 * the grass), no offset. Rounding is a small FIXED chamfer at each corner: it eases the
 * corners without smoothing diagonal cell stairsteps into big curves. Used for the water
 * body fill AND each eroded depth band (trace the eroded set the same way).
 */
interface Edge { fx: number; fy: number; tx: number; ty: number; used: boolean; }
export function waterContours(cells: ReadonlySet<string>): WaterLoop[] {
  if (!cells || cells.size === 0) return [];
  const filled = (wx: number, wy: number) => cells.has(`${wx},${wy}`);
  const pt = (px: number, py: number) => `${px},${py}`;
  // MULTIPLE edges can start at the same lattice vertex (a pinch/diagonal touch), so key a
  // LIST per start point — never overwrite (the old single-map dropped edges and merged a
  // hole into the outer loop, so plant carves vanished).
  const byStart = new Map<string, Edge[]>();
  const all: Edge[] = [];
  const add = (fx: number, fy: number, tx: number, ty: number) => {
    const e: Edge = { fx, fy, tx, ty, used: false };
    all.push(e);
    const k = pt(fx, fy);
    let list = byStart.get(k);
    if (!list) { list = []; byStart.set(k, list); }
    list.push(e);
  };
  for (const key of cells) {
    const [wx, wy] = key.split(",").map(Number) as [number, number];
    if (!filled(wx, wy - 1)) add(wx, wy, wx + 1, wy);             // top  → +x
    if (!filled(wx + 1, wy)) add(wx + 1, wy, wx + 1, wy + 1);     // right→ +y
    if (!filled(wx, wy + 1)) add(wx + 1, wy + 1, wx, wy + 1);     // bottom→ −x
    if (!filled(wx - 1, wy)) add(wx, wy + 1, wx, wy);             // left → −y
  }
  // Walk directed edges into closed loops. At a vertex with several outgoing edges, pick
  // the one that turns most CLOCKWISE relative to the incoming direction (hug the wall) so
  // outer loops and holes stay separate.
  const loops: Array<Array<[number, number]>> = [];
  for (const start of all) {
    if (start.used) continue;
    let cur: Edge | undefined = start;
    const loop: Array<[number, number]> = [];
    let guard = 0;
    while (cur && !cur.used && guard++ < 1_000_000) {
      cur.used = true;
      loop.push([cur.fx, cur.fy]);
      const inDx = cur.tx - cur.fx, inDy = cur.ty - cur.fy;
      const cands: Edge[] = (byStart.get(pt(cur.tx, cur.ty)) ?? []).filter((e) => !e.used);
      if (cands.length === 0) { cur = undefined; break; }
      // choose the most-clockwise turn (minimise signed angle from the incoming dir)
      let best: Edge = cands[0]!, bestScore = Infinity;
      for (const e of cands) {
        const dx = e.tx - e.fx, dy = e.ty - e.fy;
        // turn angle: atan2 of the cross/dot; prefer the smallest (rightmost) turn
        const cross = inDx * dy - inDy * dx;
        const dot = inDx * dx + inDy * dy;
        let ang = Math.atan2(cross, dot);          // (−π, π]; right turns negative
        if (ang <= -Math.PI + 1e-9) ang = Math.PI; // treat a U-turn as the last resort
        if (ang < bestScore) { bestScore = ang; best = e; }
      }
      cur = best;
    }
    if (loop.length >= 4) loops.push(loop);
  }
  // collapse collinear runs → keep only real corners
  const dedup = (loop: Array<[number, number]>): Array<[number, number]> => {
    const out: Array<[number, number]> = []; const n = loop.length;
    for (let i = 0; i < n; i++) {
      const a = loop[(i - 1 + n) % n]!, b = loop[i]!, c = loop[(i + 1) % n]!;
      const ax = b[0] - a[0], ay = b[1] - a[1], bx = c[0] - b[0], by = c[1] - b[1];
      if (ax * by - ay * bx !== 0 || ax * bx + ay * by <= 0) out.push(b);
    }
    return out.length >= 3 ? out : loop;
  };
  // Small fixed-radius corner chamfer: replace each corner with two points inset a SMALL
  // distance back along each adjacent edge (capped well below half the edge). Kept small so
  // the outline hugs the cells — water still reaches the plot edge and diagonal cell
  // stairsteps aren't pulled inward into a big curve; corners are just lightly eased.
  const R = 0.42;   // lattice units (~0.4 cell)
  const round = (loop: Array<[number, number]>): Array<[number, number]> => {
    const n = loop.length; if (n < 3) return loop;
    const out: Array<[number, number]> = [];
    for (let i = 0; i < n; i++) {
      const a = loop[(i - 1 + n) % n]!, b = loop[i]!, c = loop[(i + 1) % n]!;
      let ax = a[0] - b[0], ay = a[1] - b[1]; const la = Math.hypot(ax, ay) || 1;
      let cx = c[0] - b[0], cy = c[1] - b[1]; const lc = Math.hypot(cx, cy) || 1;
      ax /= la; ay /= la; cx /= lc; cy /= lc;
      const da = Math.min(R, la * 0.4), dc = Math.min(R, lc * 0.4);
      out.push([b[0] + ax * da, b[1] + ay * da]);
      out.push([b[0] + cx * dc, b[1] + cy * dc]);
    }
    return out;
  };
  return loops.map((raw) => round(dedup(raw)).map(([x, y]) => ({ x: x * WATER_CELL, y: y * WATER_CELL })));
}

/** Radius (garden units) kept clear of water around a plant's BASE. The blooms sit high
 *  above the ground (drawn in screen-space above the stem), so water only needs to clear the
 *  small footprint where the stem meets the soil — a tight ring, not the whole bloom spread.
 *  Kept just above the collision base so water can lap right up to the plant. */
export const PLANT_WATER_CLEARANCE = PLACE_RADIUS + 0.35;

/** Is water-cell centre (cgx,cgy) within any plant's water-clearance? */
function underAnyPlant(placements: ReadonlyArray<GardenPlacement>, cgx: number, cgy: number): boolean {
  const r2 = PLANT_WATER_CLEARANCE * PLANT_WATER_CLEARANCE;
  for (const p of placements) {
    if ((p.gx - cgx) ** 2 + (p.gy - cgy) ** 2 <= r2) return true;
  }
  return false;
}

/** Extra garden-unit ring kept clear of water AROUND a decor item's footprint when PAINTING water, so the
 *  brush flows AROUND a placed object (leaving a dry island) instead of over its base. This applies ONLY
 *  to the paint brush — decor placement itself can't overlap water (canPlaceDecorAt), so we never carve
 *  already-painted water when decor is placed (that would retroactively reshape the pond). */
export const DECOR_WATER_MARGIN = 0.3;

/** Multiplier on a decor item's footprint for the WATER clearance at placement time: the drawn art + its
 *  soft contact shadow extend beyond the footprint, so keep the item this much further from water to be
 *  sure no drawn pixel (shadow included) touches the water. */
export const DECOR_WATER_CLEARANCE_MUL = 1.4;

/** Is water-cell centre (cgx,cgy) within any decor item's water-clearance (footprint + margin)? */
function underAnyDecor(decor: ReadonlyArray<DecorPlacement>, cgx: number, cgy: number): boolean {
  for (const d of decor) {
    const r = decorDef(d.decor).footprint + DECOR_WATER_MARGIN;
    if ((d.gx - cgx) ** 2 + (d.gy - cgy) ** 2 <= r * r) return true;
  }
  return false;
}

/**
 * The EFFECTIVE water cells = painted cells MINUS any cell under a plant's clearance, clamped to the plot.
 * This is the single source of truth for the rendered + collidable pool. It does NOT carve around decor:
 * decor can't be placed overlapping water (so there's nothing to carve), and the paint brush already flows
 * around placed decor — so the stored water simply has an island where the decor is. (Carving here would
 * retroactively reshape existing water when a decor item is placed near it — which we don't want.)
 */
export function effectiveWaterCells(
  water: ReadonlySet<string>, placements: ReadonlyArray<GardenPlacement>, plot?: Plot,
): Set<string> {
  const out = new Set<string>();
  for (const key of water) {
    const [wx, wy] = key.split(",").map(Number) as [number, number];
    // CLAMP to the plot: drop any cell whose box pokes past the green (e.g. old saves,
    // or cells left after the plot shrank) so the rendered water never spills off-plot.
    if (plot && !cellInPlot(plot, wx, wy)) continue;
    const cc = waterCellCenter(wx, wy);
    if (placements.length && underAnyPlant(placements, cc.gx, cc.gy)) continue;
    out.add(key);
  }
  return out;
}

/**
 * The water cells a BRUSH covers around (gx, gy), restricted to inside the plot AND
 * excluding any cell under a plant's clearance — so you can't paint water under a flower
 * (its footprint stays dry). For ERASING the plant exclusion is irrelevant (we're removing
 * cells), so it's skipped. Plants also can't be placed/moved onto water (canPlaceAt rejects
 * it), so flowers never end up in water. `round` is sized by `radius` (garden units); `block` ignores it and takes `blockCells` whole cells per
 * side snapped to the cell under the cursor (odd → centred, even → biased +, like the stone
 * path's block in sim/terrain.ts).
 */
export function brushWaterCells(
  plot: Plot, placements: GardenPlacement[], gx: number, gy: number, radius: number, forErase = false,
  decor: ReadonlyArray<DecorPlacement> = [], shape: WaterBrushShape = "round", blockCells = WATER_BLOCK_CELLS,
): string[] {
  const out: string[] = [];
  const r = Math.max(WATER_CELL * 0.5, radius);
  let c0 = waterCellAt(gx - r, gy - r), c1 = waterCellAt(gx + r, gy + r);
  if (shape === "block") {
    const n = Math.max(1, Math.round(blockCells)), c = waterCellAt(gx, gy);
    const lo = -Math.floor((n - 1) / 2), hi = Math.ceil((n - 1) / 2);
    c0 = { wx: c.wx + lo, wy: c.wy + lo }; c1 = { wx: c.wx + hi, wy: c.wy + hi };
  }
  for (let wx = c0.wx; wx <= c1.wx; wx++) {
    for (let wy = c0.wy; wy <= c1.wy; wy++) {
      const cc = waterCellCenter(wx, wy);
      if (shape === "round" && (cc.gx - gx) ** 2 + (cc.gy - gy) ** 2 > r * r) continue;
      // PAINTING: only add cells whose whole box is in the plot (no spill onto the soil), not under a
      // plant, and not in a decor item's dry safe zone. ERASING: remove ANY covered cell (so straddling/
      // old cells outside the new alignment can still be cleared).
      if (!forErase) {
        if (!cellInPlot(plot, wx, wy)) continue;
        if (placements.length && underAnyPlant(placements, cc.gx, cc.gy)) continue;
        if (decor.length && underAnyDecor(decor, cc.gx, cc.gy)) continue;
      }
      out.push(waterKey(wx, wy));
    }
  }
  return out;
}

/** The DEFAULT starting water (a soft natural pond), expressed in the new water-cell
 *  system so the player can extend or erase it. A slightly wobbly ellipse near the
 *  centre-front of the first plot cell. */
export function startingWaterCells(): string[] {
  const cgx = 1.6, cgy = 1.1, rx = 2.0, ry = 1.7;   // garden units (centre + radii)
  const out: string[] = [];
  const c0 = waterCellAt(cgx - rx - 0.5, cgy - ry - 0.5), c1 = waterCellAt(cgx + rx + 0.5, cgy + ry + 0.5);
  for (let wx = c0.wx; wx <= c1.wx; wx++) {
    for (let wy = c0.wy; wy <= c1.wy; wy++) {
      const cc = waterCellCenter(wx, wy);
      // a gently lobed ellipse so the smoothed pond looks natural, not a perfect oval
      const ang = Math.atan2(cc.gy - cgy, cc.gx - cgx);
      const wob = 1 + 0.12 * Math.sin(ang * 3) + 0.08 * Math.cos(ang * 5);
      const dx = (cc.gx - cgx) / (rx * wob), dy = (cc.gy - cgy) / (ry * wob);
      if (dx * dx + dy * dy <= 1) out.push(waterKey(wx, wy));
    }
  }
  return out;
}

/** Defensive coerce of persisted painted water: the packed row-run string (`packCells`) or the
 *  pre-packing per-key array — both are live on the wire, see the note at the WATER section head. */
export function coerceWater(raw: unknown): string[] {
  if (typeof raw === "string") return unpackCells(raw);
  if (!Array.isArray(raw)) return [];
  return raw.filter((k): k is string => typeof k === "string" && /^-?\d+,-?\d+$/.test(k));
}

// ---- the packed (row-run) form of a sub-cell set ----------------------------
// `"wy:x+len,x;wy:…"` — rows by `;`, runs by `,`, a run is its first x and its length (`+len`
// omitted for a single cell). `+`/`:` separate rather than `-` because coordinates go negative.
// Shared by water (one string) and terrain (one string per type, sim/terrain.ts).

/** Most cells one packed string may unpack to. Ten times the largest legal garden
 *  (MAX_PLOT_CELLS × WATER_DIV²) — generous for stray off-plot cells after a plot sale, but a
 *  hard stop for a hostile/corrupt `"0:0+999999999"`, which would otherwise hang a visitor's tab.
 *  server/index.js enforces the same number when it coerces a save. */
export const MAX_UNPACKED_CELLS = MAX_PLOT_CELLS * WATER_DIV * WATER_DIV * 10;

/** Pack a set of `"wx,wy"` keys into row runs. Order-independent; malformed keys are dropped. */
export function packCells(keys: Iterable<string>): string {
  const rows = new Map<number, number[]>();
  for (const k of keys) {
    const i = k.indexOf(",");
    const x = Number(k.slice(0, i)), y = Number(k.slice(i + 1));
    if (i < 0 || !Number.isInteger(x) || !Number.isInteger(y)) continue;
    let r = rows.get(y);
    if (!r) rows.set(y, (r = []));
    r.push(x);
  }
  const out: string[] = [];
  for (const y of [...rows.keys()].sort((a, b) => a - b)) {
    const xs = rows.get(y)!.sort((a, b) => a - b);
    const runs: string[] = [];
    for (let i = 0; i < xs.length;) {
      let j = i;
      while (j + 1 < xs.length && xs[j + 1]! <= xs[j]! + 1) j++;   // `<=` folds duplicate keys into the run
      const len = xs[j]! - xs[i]! + 1;
      runs.push(len === 1 ? `${xs[i]}` : `${xs[i]}+${len}`);
      i = j + 1;
    }
    out.push(`${y}:${runs.join(",")}`);
  }
  return out.join(";");
}

/** Unpack row runs back into `"wx,wy"` keys. Malformed rows/runs are skipped, never thrown on, and
 *  the total is capped at MAX_UNPACKED_CELLS. */
export function unpackCells(packed: string): string[] {
  const out: string[] = [];
  if (!packed) return out;
  for (const row of packed.split(";")) {
    const m = /^(-?\d+):(.*)$/.exec(row);
    if (!m) continue;
    const y = Number(m[1]);
    for (const run of m[2]!.split(",")) {
      const r = /^(-?\d+)(?:\+(\d+))?$/.exec(run);
      if (!r) continue;
      const x0 = Number(r[1]), len = r[2] === undefined ? 1 : Number(r[2]);
      for (let k = 0; k < len; k++) {
        if (out.length >= MAX_UNPACKED_CELLS) return out;
        out.push(`${x0 + k},${y}`);
      }
    }
  }
  return out;
}

/**
 * Can a plant be placed at (gx, gy)? It must be inside the plot (with an edge margin),
 * clear of the water (the painted cell set), not collide with an existing placement
 * (within 2·PLACE_RADIUS), and not stand on a fence line. `ignoreId` skips one (re-positioning).
 *
 * Plants with a `hostId` are not obstacles: they store their HOST's position, not a ground
 * position of their own, and the host's decor footprint already blocks that ground.
 */
export function canPlaceAt(
  plot: Plot, placements: GardenPlacement[], gx: number, gy: number,
  water?: ReadonlySet<string>, ignoreId?: string, decor?: DecorPlacement[], fences?: ReadonlySet<string>,
): boolean {
  if (!inPlot(plot, gx, gy, PLACE_EDGE_MARGIN)) return false;
  // a FENCE is solid: a plant's base may not stand on the line (see decorPlaceBlock for why)
  if (discHitsFence(fences, gx, gy, PLACE_RADIUS)) return false;
  // keep the plant's footprint (base) clear of the water — not just its centre point.
  if (water && waterWithin(water, gx, gy, PLACE_RADIUS)) return false;
  // (the starting oak is now a normal decor item, so its trunk is handled by the decor-footprint loop below.)
  const minD2 = (PLACE_RADIUS * 2) ** 2;
  for (const p of placements) {
    if (p.id === ignoreId) continue;
    if (p.hostId) continue;          // hosted plants sit AT their host — the host's footprint speaks for them
    const d2 = (p.gx - gx) ** 2 + (p.gy - gy) ** 2;
    if (d2 < minD2) return false;
  }
  // don't overlap placed decor (each decor's footprint + the plant's own radius).
  if (decor) for (const d of decor) {
    if (d.id === ignoreId) continue;
    const rr = decorDef(d.decor).footprint + PLACE_RADIUS;
    if ((d.gx - gx) ** 2 + (d.gy - gy) ** 2 < rr * rr) return false;
  }
  return true;
}

/** WHY a decor item can't go where it is: past the plot's outer edge, too near the water, overlapping
 *  another plant or decor item, or standing on a fence. The place flow names this in its toast (see
 *  decorPlaceBlock). */
export type DecorBlock = "edge" | "water" | "overlap" | "fence";

/**
 * Can a DECOR item be placed at (gx, gy)? Same rules as canPlaceAt but using the decor item's own
 * footprint against the plot edge, water, tree, other placed plants, other decor and the fences.
 * `ignoreId` skips one (re-positioning a decor instance).
 */
export function canPlaceDecorAt(
  plot: Plot, decorFootprint: number, placements: GardenPlacement[], decor: DecorPlacement[],
  gx: number, gy: number, water?: ReadonlySet<string>, ignoreId?: string, waterClearance?: number,
  fences?: ReadonlySet<string>,
): boolean {
  return decorPlaceBlock(plot, decorFootprint, placements, decor, gx, gy, water, ignoreId, waterClearance, fences) === null;
}

/** canPlaceDecorAt's rules, returning the FIRST rule broken (edge → water → fence → overlap), or null if clear.
 *
 *  THE FENCE RULE. A fence is drawn as flat 2D pieces depth-sorted against everything else, and no draw
 *  order is right for an item whose footprint STRADDLES the fence line — part of it is in front of the
 *  fence and part behind, so the fountain's basin either covers the rails it should sit behind or is cut by
 *  them. So the line is solid: no footprint may come within FENCE_HALF_T of it (fence.ts fenceBuildBlock
 *  is the same test from the fence's side). Everything that IS clear of the line then sorts exactly — see
 *  the fence depth solve in GardenGroundLayer.drawFences. */
export function decorPlaceBlock(
  plot: Plot, decorFootprint: number, placements: GardenPlacement[], decor: DecorPlacement[],
  gx: number, gy: number, water?: ReadonlySet<string>, ignoreId?: string, waterClearance?: number,
  fences?: ReadonlySet<string>,
): DecorBlock | null {
  if (!inPlot(plot, gx, gy, PLACE_EDGE_MARGIN)) return "edge";
  // NO part of the drawn item — including its soft contact SHADOW, which extends well past the footprint —
  // may touch water. The caller passes `waterClearance` = the item's real drawn+shadow radius in garden
  // units (see decorWaterClearance); fall back to a footprint multiple if not supplied.
  const clear = waterClearance ?? decorFootprint * DECOR_WATER_CLEARANCE_MUL;
  if (water && waterWithin(water, gx, gy, clear)) return "water";
  if (discHitsFence(fences, gx, gy, decorFootprint)) return "fence";
  // (the starting oak is a normal decor item now — handled by the decor-vs-decor loop below.)
  for (const p of placements) {
    // A HOSTED plant stores its host's gx,gy, so a planter box being nudged used to collide with the very
    // plants growing in it — any move shorter than footprint + PLACE_RADIUS snapped back as invalid. The
    // host's own footprint already covers everything standing in it, so hosted plants never block decor.
    if (p.hostId) continue;
    const rr = decorFootprint + PLACE_RADIUS;
    if ((p.gx - gx) ** 2 + (p.gy - gy) ** 2 < rr * rr) return "overlap";
  }
  for (const d of decor) {
    if (d.id === ignoreId) continue;
    const rr = decorFootprint + decorDef(d.decor).footprint;
    if ((d.gx - gx) ** 2 + (d.gy - gy) ** 2 < rr * rr) return "overlap";
  }
  return null;
}

/** The decor ghost's footprint disc is read as a polar grid of RINGS × SECTORS cells; cell index is
 *  `ring * FOOTPRINT_SECTORS + sector`, ring 0 innermost, sector 0 starting at garden angle 0 (+gx). */
export const FOOTPRINT_RINGS = 3;
export const FOOTPRINT_SECTORS = 24;

/**
 * WHICH PART of a decor item's footprint disc is blocked at (gx, gy) — the cells of the polar grid above
 * that fall inside something the placement rules forbid. Empty exactly when decorPlaceBlock is null.
 *
 * The regions match the rules one for one, so the red on the ghost is the rule made visible:
 *   · overlap — the footprint disc ∩ each colliding item's own disc (their centres are < the two radii apart);
 *   · water   — the disc ∩ the water's keep-out halo: cell centres within (clearance − footprint) of a point,
 *               which is non-empty exactly when a centre is within `clearance` of the item's base;
 *   · fence   — the disc ∩ the fence's own thickness: cells within FENCE_HALF_T of a fence line;
 *   · edge    — the rule tests the base point only, so while the base is in, nothing is red; once it is out,
 *               every cell whose point is past the edge margin.
 * A shallow overlap can fall between sample points, so every blocker also lights the rim cell facing it:
 * the ghost never reads "invalid" with no red on it. Only ever called when the spot is invalid, and only
 * on pointer move — never per frame.
 */
export function decorBlockedCells(
  plot: Plot, decorFootprint: number, placements: GardenPlacement[], decor: DecorPlacement[],
  gx: number, gy: number, water?: ReadonlySet<string>, ignoreId?: string, waterClearance?: number,
  fences?: ReadonlySet<string>,
): number[] {
  const fp = decorFootprint;
  const K = FOOTPRINT_RINGS, S = FOOTPRINT_SECTORS, TAU = Math.PI * 2;
  const flagged = new Uint8Array(K * S);
  const sample = (k: number, a: number) => {
    const r = ((k + 0.5) / K) * fp;
    return { x: gx + Math.cos(a) * r, y: gy + Math.sin(a) * r };
  };
  const sectorOf = (a: number) => ((Math.floor(a / (TAU / S)) % S) + S) % S;
  // A round blocker at (bx, by): every cell whose point is within `rad` of it, plus the rim cell facing it.
  const disc = (bx: number, by: number, rad: number) => {
    const r2 = rad * rad;
    for (let k = 0; k < K; k++) for (let s = 0; s < S; s++) {
      const p = sample(k, ((s + 0.5) / S) * TAU);
      if ((p.x - bx) ** 2 + (p.y - by) ** 2 < r2) flagged[k * S + s] = 1;
    }
    flagged[(K - 1) * S + sectorOf(Math.atan2(by - gy, bx - gx))] = 1;
  };
  for (const p of placements) {
    if (p.hostId) continue;
    const rr = fp + PLACE_RADIUS;
    if ((p.gx - gx) ** 2 + (p.gy - gy) ** 2 < rr * rr) disc(p.gx, p.gy, PLACE_RADIUS);
  }
  for (const d of decor) {
    if (d.id === ignoreId) continue;
    const their = decorDef(d.decor).footprint;
    const rr = fp + their;
    if ((d.gx - gx) ** 2 + (d.gy - gy) ** 2 < rr * rr) disc(d.gx, d.gy, their);
  }
  if (water) {
    const clear = waterClearance ?? fp * DECOR_WATER_CLEARANCE_MUL;
    const halo = Math.max(0, clear - fp), c2 = clear * clear;
    const c0 = waterCellAt(gx - clear, gy - clear), c1 = waterCellAt(gx + clear, gy + clear);
    for (let wx = c0.wx; wx <= c1.wx; wx++) for (let wy = c0.wy; wy <= c1.wy; wy++) {
      if (!water.has(waterKey(wx, wy))) continue;
      const cc = waterCellCenter(wx, wy);
      if ((cc.gx - gx) ** 2 + (cc.gy - gy) ** 2 <= c2) disc(cc.gx, cc.gy, halo);
    }
    // the base standing IN a water cell whose centre is beyond the clearance still breaks the rule
    if (isWaterAt(water, gx, gy)) for (let s = 0; s < S; s++) flagged[s] = 1;
  }
  // the cells standing on a fence's thickness, plus the rim cell facing each fence's nearest point
  const nearFences = fencesNear(fences, gx, gy, fp + FENCE_HALF_T);
  if (nearFences.length) {
    for (let k = 0; k < K; k++) for (let s = 0; s < S; s++) {
      const p = sample(k, ((s + 0.5) / S) * TAU);
      if (fencesNear(fences, p.x, p.y, FENCE_HALF_T).length) flagged[k * S + s] = 1;
    }
    for (const h of nearFences) flagged[(K - 1) * S + sectorOf(Math.atan2(h.ny - gy, h.nx - gx))] = 1;
  }
  if (!inPlot(plot, gx, gy, PLACE_EDGE_MARGIN)) {
    let any = false;
    for (let k = 0; k < K; k++) for (let s = 0; s < S; s++) {
      const p = sample(k, ((s + 0.5) / S) * TAU);
      if (!inPlot(plot, p.x, p.y, PLACE_EDGE_MARGIN)) { flagged[k * S + s] = 1; any = true; }
    }
    // just past the margin, the base can be out while every sample point is still in: light the middle
    if (!any) for (let s = 0; s < S; s++) flagged[s] = 1;
  }
  const out: number[] = [];
  for (let i = 0; i < flagged.length; i++) if (flagged[i]) out.push(i);
  return out;
}

/** A short unique id for a new placement (timestamp + counter passed in by caller). */
export function placementId(seed: number, n: number): string {
  return `gp_${seed.toString(36)}_${n.toString(36)}`;
}

// ── PLANTING HOSTS: planter/basket/urn decor that holds a plant per SLOT. Every host on the roster holds
//    ONE except the long planter box, which holds THREE in a row (see DecorDef.host / hostSlotX). A plant
//    with `hostId` set renders on that host's slot (see the render layer) and takes the host's position.
//    Trailing-form plants REQUIRE a host (they'd hang into the dirt on flat ground); any plant MAY be
//    planted on a host (looks potted). ──

/** The first FREE slot index on this host, or -1 if every slot is taken. `ignoreId` skips a plant id, so
 *  re-homing a plant that already sits on this host doesn't see its own slot as occupied.
 *
 *  A plant with no `slot` counts as slot 0 — that is what every save written before multi-slot hosts
 *  holds, and it is the slot those plants have always rendered in. */
export function freeHostSlot(d: DecorPlacement, placements: GardenPlacement[], ignoreId?: string): number {
  if (!isHost(d.decor)) return -1;
  const n = hostSlots(d.decor);
  const taken = new Set<number>();
  for (const p of placements) {
    if (p.hostId !== d.id || p.id === ignoreId) continue;
    taken.add(Math.min(n - 1, Math.max(0, Math.round(p.slot ?? 0))));
  }
  for (let i = 0; i < n; i++) if (!taken.has(i)) return i;
  return -1;
}

/** True if the decor instance `d` is a planting host with at least one free slot. */
export function isHostEmpty(d: DecorPlacement, placements: GardenPlacement[]): boolean {
  return freeHostSlot(d, placements) >= 0;
}

/** How far round a slot's soil point a dropped plant still lands in it, in the host's LOCAL art px (scaled
 *  with the host's live art size by the caller). Wide enough that a player aiming "into the planter" never
 *  has to find a pixel; tall enough to take a base held a little above the rim or over its front face. A
 *  multi-slot host's zones are half a slot pitch wide instead, so neighbouring holes never overlap. */
export const SOIL_REACH_X = 42;
export const SOIL_REACH_Y = 34;

/**
 * THE PLANTER SLOT A PLANT IS BEING DROPPED INTO — found by its SOIL, on screen.
 *
 * `base` is where the plant's base would stand (screen px, any origin), and `soilOf(d, slot)` is where that
 * slot's soil is in the SAME space, with `k` the host's live art scale. The nearest free slot whose soil zone
 * holds the base wins, so on the long planter box the hole you aim at is the hole you get.
 *
 * This replaced a test against the host's GROUND footprint: the cursor was projected to the ground and had
 * to land near the planter's foot. The soil is 60-140 px up the planter, so a plant held over the soil —
 * the natural gesture — projected to ground well behind the planter and never snapped; it only snapped when
 * aimed at the planter's base. `ignoreId` skips a plant (re-homing one that is already on a host).
 */
export function hostSoilAt(
  decor: DecorPlacement[], placements: GardenPlacement[], base: { x: number; y: number },
  soilOf: (d: DecorPlacement, slot: number) => { x: number; y: number; k: number }, ignoreId?: string,
): (DecorPlacement & { slot: number }) | null {
  let best: (DecorPlacement & { slot: number }) | null = null;
  let bestE = Infinity;
  for (const d of decor) {
    if (!isHost(d.decor)) continue;
    const n = hostSlots(d.decor);
    const taken = new Set<number>();
    for (const p of placements) {
      if (p.hostId !== d.id || p.id === ignoreId) continue;
      taken.add(Math.min(n - 1, Math.max(0, Math.round(p.slot ?? 0))));
    }
    const dx0 = decorDef(d.decor).host?.slotDx ?? 0;
    for (let i = 0; i < n; i++) {
      if (taken.has(i)) continue;
      const s = soilOf(d, i);
      const rx = (n > 1 && dx0 > 0 ? dx0 * 0.5 : SOIL_REACH_X) * s.k, ry = SOIL_REACH_Y * s.k;
      const e = ((base.x - s.x) / rx) ** 2 + ((base.y - s.y) / ry) ** 2;
      if (e <= 1 && e < bestE) { bestE = e; best = { ...d, slot: i }; }
    }
  }
  return best;
}

/** Can this species be planted on the GROUND (vs requiring a host)? Trailing plants can't. */
export function canPlantOnGround(species: FlowerId): boolean {
  return !isTrailing(species);
}

/** Defensive coerce of a persisted placements array (used by save + server). */
export function coercePlacements(raw: unknown): GardenPlacement[] {
  if (!Array.isArray(raw)) return [];
  const out: GardenPlacement[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const species = typeof o.species === "string" ? o.species : null;
    const gx = typeof o.gx === "number" ? o.gx : null;
    const gy = typeof o.gy === "number" ? o.gy : null;
    if (species == null || gx == null || gy == null) continue;
    const id = typeof o.id === "string" && o.id ? o.id : `gp_${out.length}`;
    const hostId = typeof o.hostId === "string" && o.hostId ? o.hostId : undefined;
    // slot is only meaningful with a host; absent (every save predating multi-slot hosts) reads as 0,
    // which for a one-slot host is the rim itself — so those plants keep the position they had.
    const slot = hostId && typeof o.slot === "number" && Number.isFinite(o.slot)
      ? Math.max(0, Math.round(o.slot)) : undefined;
    out.push({ id, species: species as FlowerId, gx, gy, ...(hostId ? { hostId } : {}), ...(slot ? { slot } : {}) });
  }
  return out;
}

/**
 * LOAD MIGRATION: lift every plant whose host decor is NO LONGER A HOST back into the bag.
 *
 * A retheme can take `host` off a decor id without renaming it (the `wallTrough`, now a flower hamper that
 * draws its own blooms — see its entry in decor.ts). A plant saved on one of those still carries `hostId`
 * and its host's gx/gy, so without this it rendered on the grass INSIDE the hamper's footprint, a spot the
 * placement rules would never allow — and a trailing plant, which may not stand on the ground at all,
 * stood there too. Dropping the placement is the whole refund: the bag reads `stock − placed`, so the
 * plant is simply back in the inventory to plant somewhere real. Plants whose host id is MISSING from the
 * decor list are left alone — that is a different state (mid-move, spectate) and not this migration's call.
 */
export function liftFromRetiredHosts(placements: GardenPlacement[], decor: DecorPlacement[]): GardenPlacement[] {
  const byId = new Map(decor.map((d) => [d.id, d]));
  return placements.filter((p) => {
    const host = p.hostId ? byId.get(p.hostId) : undefined;
    return !host || isHost(host.decor);
  });
}

/**
 * Defensive coerce of a persisted Plot (a list of integer cells, always ≥1 cell).
 *
 * AN OVER-CAP SAVE IS LOADED AT ITS TRUE SIZE AND NOT TRUNCATED. `MAX_PLOT_CELLS` gates BUYING; it is not
 * applied here, deliberately. A save can legitimately hold more than the cap — a dev account, or a player
 * rolled back from a future expansion that shipped a bigger number — and clipping it would silently
 * DESTROY ground the player owns, orphaning every plant, decor item, fence and painted water cell standing
 * on the cells dropped, with no way to get them back. Worse, an arbitrary slice of the cell list can leave
 * the plot DISCONNECTED (`isConnected`), which nothing downstream expects. So it loads whole, it renders
 * whole, and the only consequence of being over the cap is that the expand tool offers nothing (see
 * `plotAtMax`) until the player sells back down under it.
 */
export function coercePlot(raw: unknown): Plot {
  const cells: Array<{ cx: number; cy: number }> = [];
  const arr = (raw && typeof raw === "object" && Array.isArray((raw as { cells?: unknown }).cells))
    ? (raw as { cells: unknown[] }).cells : [];
  const seen = new Set<string>();
  for (const c of arr) {
    if (!c || typeof c !== "object") continue;
    const o = c as Record<string, unknown>;
    if (typeof o.cx !== "number" || typeof o.cy !== "number") continue;
    const cx = Math.round(o.cx), cy = Math.round(o.cy);
    const k = `${cx},${cy}`;
    if (seen.has(k)) continue;
    seen.add(k);
    cells.push({ cx, cy });
  }
  if (cells.length === 0) cells.push({ cx: 0, cy: 0 }); // always at least the starting cell
  return { cells };
}
